import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import config from '../config';
import {
	getFeedbackWithInitialResponse,
	getOrderedDashboardFeedbacks,
} from '../util/dashboardUtils';

const axiosInstance = axios.create({
	baseURL: config.feedbackApi.baseUrl,
	headers: {
		timeout: '60000',
	},
});

export const dashboardFeedbackKeys = {
	allDashboardFeedbacks: ['dashboard-feedbacks'],
	dashboardFeedbackLists: () => [
		...dashboardFeedbackKeys.allDashboardFeedbacks,
		'dashboard-feedback-list',
	],
	dashboardFeedbackList: (interviewerId, params) => [
		...dashboardFeedbackKeys.dashboardFeedbackLists(),
		interviewerId,
		params,
	],
	dashboardFeedbackForm: (feedbackRequestId) => ['interviewFeedback', feedbackRequestId],
	feedbackList: (params) => ['feedbacks', 'feedbackList', params],
};

export function useGetDashboardFeedbacksListQuery(params) {
	return useQuery({
		queryKey: dashboardFeedbackKeys.dashboardFeedbackList(params.interviewerId, params),
		queryFn: async () => {
			const response = await axiosInstance.get('/proxy/feedback/interviewer-feedback-requests', {
				params,
			});
			return response.data;
		},
		select: (feedbacksList) => getOrderedDashboardFeedbacks(feedbacksList),
	});
}

export function useGetDashboardInterviewFeedbackQuery(params) {
	return useQuery({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: dashboardFeedbackKeys.dashboardFeedbackForm(params.feedbackRequestId),

		queryFn: async () => {
			const loggedInUserId = params.loggedInUserId
				? `?loggedInUserId=${params.loggedInUserId}`
				: '';

			const response = await axiosInstance.get(
				`/proxy/feedback/feedback-requests/${params.feedbackRequestId}/form${loggedInUserId}`,
			);
			return response.data;
		},
		select: (feedback) => getFeedbackWithInitialResponse(feedback),
	});
}

export function useSubmitFeedbackMutation(interviewerId, feedbackRequestId) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data) => {
			const response = await axiosInstance.post('/proxy/feedback/feedback-submit', data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: dashboardFeedbackKeys.dashboardFeedbackList(interviewerId),
			});
			queryClient.invalidateQueries({
				queryKey: dashboardFeedbackKeys.dashboardFeedbackForm(feedbackRequestId),
			});
		},
	});
}

export function useCancelFeedbackMutation(params, submittalId) {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async (data) => {
			const response = await axiosInstance.post('/proxy/feedback/feedback-cancel', data);
			return response.data;
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: dashboardFeedbackKeys.dashboardFeedbackList(String(params.interviewerId), params),
			});
			queryClient.invalidateQueries({
				queryKey: dashboardFeedbackKeys.feedbackList({
					customerId: params.customerId,
					submittalId: String(submittalId),
				}),
			});
		},
	});
}
